import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import some from 'lodash/some';
import {
  AmountContainer,
  AmountData,
  AmountHeader,
  AmountHeaderMobile,
  CancelButton,
  CancelContainer,
  CancelHeader,
  CancelHeaderMobile,
  CancelLoadingContainer,
  DescriptionContainer,
  NoRecentPayments,
  PaymentIdContainer,
  PaymentIdData,
  PaymentIdHeader,
  PaymentIdHeaderMobile,
  PendingScheduledPaymentsContainer,
  ReceiptDateContainer,
  ReceiptDateData,
  ReceiptDateHeader,
  ReceiptDateHeaderMobile,
  ReceiptDescriptionData,
  ReceiptDescriptionHeader,
  ReceiptDescriptionHeaderMobile,
  ShowHidePendingPaymentsContainer,
  ShowPaymentsButton,
  StatusContainer,
  StatusData,
  StatusHeader,
  StatusHeaderMobile,
} from './styles';
import { DISPLAY_DATE_FORMAT } from '../../common/constants';
import { setPolicyAccountsAndMerge, setStatementAccountsAndMerge } from './utils';
import { useSnackbar } from 'notistack';
import { connect } from 'react-redux';
import { setLoading } from './accountDetailsReducer';
import Loader from '../../common/loader';
import { cancelScheduledPayment } from './policyAPI';
import { getCustomerBillingInformation } from '../dashboard/billing/billingAPI';

export const ScheduledPendingPaymentsRow = (props) => {
  const {
    billingAccountsCountLimit,
    billingStatusCd,
    cancelFutureScheduledPayments,
    customerId,
    dispatch,
    isLoading,
    hasOpenBalance,
    fullAccount,
    paymentTypes,
  } = props;

  const { enqueueSnackbar } = useSnackbar();
  const [paymentTypesMap, setPaymentTypesMap] = React.useState(new Map());
  const [showAllRecentPayments, setShowRecentPayments] = React.useState(false);
  const [isCancellingScheduledPayment, setIsCancellingScheduledPayment] = React.useState(false);
  const [paymentBeingCancelled, setPaymentBeingCancelled] = React.useState('');
  const cancelPayment = async (receiptId) => {
    if (window.confirm('Are you sure you want to cancel this scheduled payment?')) {
      setIsCancellingScheduledPayment(true);
      setPaymentBeingCancelled(receiptId);
      await dispatch(setLoading(true));
      await dispatch(cancelScheduledPayment(fullAccount.systemId, receiptId, enqueueSnackbar));
      await dispatch(
        getCustomerBillingInformation(
          customerId,
          hasOpenBalance,
          billingAccountsCountLimit,
          billingStatusCd,
        ),
      );
      setPaymentBeingCancelled('');
      setIsCancellingScheduledPayment(false);
      await dispatch(setLoading(false));
    }
  };
  const isActionAvailable = (merged) => {
    return some(merged, (payment) => {
      return payment.statusCd.includes('Scheduled');
    });
  };
  React.useEffect(() => {
    if (paymentTypes) {
      const entries = Object.entries(paymentTypes);
      if (entries.length > 0) {
        setPaymentTypesMap(new Map(Object.entries(paymentTypes)));
      }
    }
  }, [paymentTypes]);
  const setStatementAccounts = () => {
    let merged = setStatementAccountsAndMerge(fullAccount, paymentTypesMap, true);

    return (
      <>
        <PendingScheduledPaymentsContainer>
          <PaymentIdHeader>Payment Id</PaymentIdHeader>
          <ReceiptDescriptionHeader>Type</ReceiptDescriptionHeader>
          <ReceiptDateHeader>Date</ReceiptDateHeader>
          <AmountHeader>Amount </AmountHeader>
          <StatusHeader>Status</StatusHeader>
          {isActionAvailable(merged) && cancelFutureScheduledPayments && (
            <CancelHeader>Action</CancelHeader>
          )}
        </PendingScheduledPaymentsContainer>
        {merged.length > 0 ? (
          merged
            .filter((_item, index) => (showAllRecentPayments ? index <= merged.length : index < 5))
            .map((payment, index) => {
              return (
                <PendingScheduledPaymentsContainer key={index}>
                  <PaymentIdContainer>
                    <PaymentIdHeaderMobile>Payment Id</PaymentIdHeaderMobile>
                    <PaymentIdData>{payment.receiptId}</PaymentIdData>
                  </PaymentIdContainer>
                  <DescriptionContainer>
                    <ReceiptDescriptionHeaderMobile>Type</ReceiptDescriptionHeaderMobile>
                    <ReceiptDescriptionData>{payment.typeCd}</ReceiptDescriptionData>
                  </DescriptionContainer>
                  <ReceiptDateContainer>
                    <ReceiptDateHeaderMobile>Date</ReceiptDateHeaderMobile>
                    <ReceiptDateData>
                      {moment(payment.receiptDt).format(DISPLAY_DATE_FORMAT)}
                    </ReceiptDateData>
                  </ReceiptDateContainer>
                  <AmountContainer>
                    <AmountHeaderMobile>Amount</AmountHeaderMobile>
                    <AmountData>${parseFloat(payment.receiptAmt).toFixed(2)}</AmountData>
                  </AmountContainer>
                  <StatusContainer>
                    <StatusHeaderMobile>Status</StatusHeaderMobile>
                    <StatusData>{payment.statusCd}</StatusData>
                  </StatusContainer>
                  {isActionAvailable(merged) && cancelFutureScheduledPayments && (
                    <CancelContainer>
                      {payment.statusCd === 'Scheduled' && cancelFutureScheduledPayments ? (
                        <CancelHeaderMobile>Cancel</CancelHeaderMobile>
                      ) : null}
                      {payment.statusCd === 'Scheduled' && cancelFutureScheduledPayments ? (
                        <CancelButton
                          onClick={() => cancelPayment(payment.receiptId)}
                          disabled={isLoading && isCancellingScheduledPayment}
                        >
                          {' '}
                          Cancel?
                        </CancelButton>
                      ) : null}
                      {isLoading &&
                        isCancellingScheduledPayment &&
                        paymentBeingCancelled === payment.receiptId && (
                          <CancelLoadingContainer style={{ display: 'inline-block' }}>
                            <Loader loaderheight="30px" loaderwidth="30px" />
                          </CancelLoadingContainer>
                        )}
                    </CancelContainer>
                  )}
                </PendingScheduledPaymentsContainer>
              );
            })
        ) : (
          <NoRecentPayments>No Pending/Scheduled Payments Available</NoRecentPayments>
        )}
      </>
    );
  };

  const setPolicyAccounts = () => {
    let merged = setPolicyAccountsAndMerge(fullAccount, paymentTypesMap, true);

    return (
      <>
        <PendingScheduledPaymentsContainer $info={'TableHeader'} $tableHeader={true}>
          <PaymentIdHeader>Payment Id</PaymentIdHeader>
          <ReceiptDescriptionHeader>Type</ReceiptDescriptionHeader>
          <ReceiptDateHeader>Date</ReceiptDateHeader>
          <AmountHeader>Amount </AmountHeader>
          <StatusHeader>Status</StatusHeader>
          {isActionAvailable(merged) && cancelFutureScheduledPayments && (
            <CancelHeader>Action</CancelHeader>
          )}
        </PendingScheduledPaymentsContainer>
        {merged.length > 0 ? (
          <>
            {merged
              .filter((_item, index) =>
                showAllRecentPayments ? index <= merged.length : index < 5,
              )
              .map((payment, index) => {
                return (
                  <PendingScheduledPaymentsContainer key={index} $info={'TableRow'}>
                    <PaymentIdContainer>
                      <PaymentIdHeaderMobile>Payment Id</PaymentIdHeaderMobile>
                      <PaymentIdData>{payment.receiptId}</PaymentIdData>
                    </PaymentIdContainer>
                    <DescriptionContainer>
                      <ReceiptDescriptionHeaderMobile>Type</ReceiptDescriptionHeaderMobile>
                      <ReceiptDescriptionData>{payment.typeCd}</ReceiptDescriptionData>
                    </DescriptionContainer>
                    <ReceiptDateContainer>
                      <ReceiptDateHeaderMobile>Date</ReceiptDateHeaderMobile>
                      <ReceiptDateData>
                        {moment(payment.receiptDt).format(DISPLAY_DATE_FORMAT)}
                      </ReceiptDateData>
                    </ReceiptDateContainer>
                    <AmountContainer>
                      <AmountHeaderMobile>Amount</AmountHeaderMobile>
                      <AmountData>${parseFloat(payment.receiptAmt).toFixed(2)}</AmountData>
                    </AmountContainer>
                    <StatusContainer>
                      <StatusHeaderMobile>Status</StatusHeaderMobile>
                      <StatusData>{payment.statusCd} </StatusData>
                    </StatusContainer>
                    {isActionAvailable(merged) && cancelFutureScheduledPayments && (
                      <CancelContainer>
                        {payment.statusCd === 'Scheduled' && cancelFutureScheduledPayments ? (
                          <CancelHeaderMobile>Cancel</CancelHeaderMobile>
                        ) : null}
                        {payment.statusCd === 'Scheduled' && cancelFutureScheduledPayments ? (
                          <CancelButton
                            onClick={() => cancelPayment(payment.receiptId)}
                            disabled={isLoading && isCancellingScheduledPayment}
                          >
                            {' '}
                            Cancel
                          </CancelButton>
                        ) : null}
                        {isLoading &&
                          isCancellingScheduledPayment &&
                          paymentBeingCancelled === payment.receiptId && (
                            <CancelLoadingContainer style={{ display: 'inline-block' }}>
                              <Loader loaderheight="30px" loaderwidth="30px" />
                            </CancelLoadingContainer>
                          )}
                      </CancelContainer>
                    )}
                  </PendingScheduledPaymentsContainer>
                );
              })}
            <ShowHidePendingPaymentsContainer>
              <ShowPaymentsButton onClick={() => setShowRecentPayments(!showAllRecentPayments)}>
                {merged.length <= 5
                  ? null
                  : showAllRecentPayments
                  ? `Hide Pending/Scheduled Payments`
                  : `Show All Pending/Scheduled Payments`}
              </ShowPaymentsButton>
            </ShowHidePendingPaymentsContainer>
          </>
        ) : (
          <NoRecentPayments>No Pending/Scheduled Payments Available</NoRecentPayments>
        )}
      </>
    );
  };

  return (
    <>
      {fullAccount.accountDisplayName === 'Statement'
        ? setStatementAccounts()
        : setPolicyAccounts()}
    </>
  );
};
ScheduledPendingPaymentsRow.propTypes = {
  billingAccounts: PropTypes.array,
  billingAccountsCountLimit: PropTypes.string,
  billingStatusCd: PropTypes.string,
  cancelFutureScheduledPayments: PropTypes.bool,
  customerId: PropTypes.string,
  dispatch: PropTypes.func,
  fullAccount: PropTypes.shape({
    underlyingBillingAccounts: PropTypes.array,
    accountDisplayName: PropTypes.string,
    billingHistory: PropTypes.array,
    pendingReceipts: PropTypes.array,
    systemId: PropTypes.string,
  }),
  hasOpenBalance: PropTypes.bool,
  isLoading: PropTypes.bool,
  paymentTypes: PropTypes.object,
  receiptId: PropTypes.string,
  systemId: PropTypes.string,
};

export const mapStateToProps = (state) => ({
  i18n: state.i18nSlice.i18n,
  billingAccounts: state.billingSlice.billingAccounts,
  billingAccountsCountLimit: state.configurationSlice.features.billingAccountsCountLimit,
  billingStatusCd: state.configurationSlice?.features?.billingStatusCd
    ? state.configurationSlice.features.billingStatusCd
    : '',
  cancelFutureScheduledPayments:
    state.configurationSlice?.features?.features?.cancelFutureScheduledPayments,
  customerId: state.loginSlice.user.customer_id,
  hasOpenBalance: state.configurationSlice.features.features.hasOpenBalance,
  isLoading: state.accountDetailsSlice.isLoading,
  paymentTypes: state.billingSlice.paymentTypes,
  pendingOrScheduledPayments:
    state.configurationSlice.features.features.pendingOrScheduledPaymentsList,
  recentPayments: state.configurationSlice.features.features.recentPaymentsList,
  underlyingPoliciesPayPlansEditable:
    state.configurationSlice.features.features.underlyingPoliciesPayPlansEditable,
});

export default connect(mapStateToProps)(ScheduledPendingPaymentsRow);
